import { TextField, useStyles, Checkbox, combine, PhoneInput, Select, Autocomplete2, AuthGoogleLinkedin } from '@webapps/styles';
import {
  useRouter,
  useMutation,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useFormik } from 'formik';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { ROUTES_PATH, KNOW_US } from 'config/constants';
import { createStudent, responseInvitationAffiliation, createBusiness, createSchool, getAddress } from '@webapps/business';
import { useState, useEffect, useRef } from 'react';
import Head from 'components/Head/Head';
import { linkpickTheme } from '@webapps/styles';
import ConfirmSignUp from './ConfirmSignUp';
import StripeContainer from 'components/StripeContainer';
import theme from './theme';

const signUpSchema = yup.object().shape({
  email: yup.string().email('E-mail invalide').required('* Requis'),
  password: yup
    .string()
    .required('* Requis')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z\d\s\W]{8,128}$/,
      'Le mot de passe doit contenir au minimum 8 caractères, une majuscule, une minuscule et un chiffre.'
    ),
  repeatPassword: yup
    .string()
    .required('* Requis')
    .test('passwords-match', 'Les mots de passe ne correspondent pas', function (value) {
      return this.parent.password === value;
    }),
  firstname: yup.string().required('* Requis'),
  lastname: yup.string().required('* Requis'),
  phone: yup.mixed().test('phone-required', '* Requis', function (value) {
    return Boolean(value && value.trim().length > 0);
  }),
  acceptCondition: yup
    .bool()
    .oneOf([true], '* Requis'),
});

// Schéma pour les organismes (École/Entreprise)
const organismeSchema = yup.object().shape({
  email: yup.string().email('E-mail invalide').required('Requis'),
  password: yup
    .string()
    .required('Requis')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z\d\s\W]{8,128}$/,
      'Le mot de passe doit contenir au minimum 8 caractères, une majuscule, une minuscule et un chiffre.'
    ),
  repeatPassword: yup
    .string()
    .required('Requis')
    .test('passwords-match', 'Les mots de passe ne correspondent pas', function (value) {
      return this.parent.password === value;
    }),
  name: yup.string().required('* Requis'),
  siret: yup.string().required('* Requis').matches(/^\d{14}$/, '14 chiffres requis'),
  phone: yup.mixed().test('phone-required', '* Requis', function (value) {
    return Boolean(value && value.trim().length > 0);
  }),
  label: yup.string().required('* Requis'),
  city: yup.string().required('* Requis'),
  cityCode: yup.string().required('* Requis'),
  subscription: yup.string().required('* Requis'),
  knowUs: yup.mixed().test('knowUs-required', '* Requis', function (value) {
    return value !== null && value !== undefined;
  }),
  acceptCondition: yup
    .boolean()
    .oneOf([true], '* Requis')
    .transform((value) => {
      if (value === 'on') return true;
      return value;
    }),
});

const {
  images: { CarrouselSign, PicsLogo, Candidate },
} = theme;

export default function SignUp() {
  const { classes, theme } = useStyles(styles);

  const { push, searchParams } = useRouter();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [accType, setAccType] = useState(location?.state?.user || searchParams?.u || 'individual');
  const [errorMessage, setErrorMessage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [errorPhone, setErrorPhone] = useState('');

  // États pour la gestion de l'abonnement
  const [subscriptionPeriodSelected, setSubscriptionPeriodSelected] = useState(1);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date;
  });
  const monthlyPrice = 36; // Prix mensuel fixe

  // Générer dynamiquement les options avec le coût total
  const subscriptionPeriodOptions = Array.from({ length: 12 }, (_, i) => {
    const months = i + 1;
    const price = months * monthlyPrice;
    return {
      value: months,
      label: `${months} mois${months > 1 ? 's' : ''} - ${price} €`
    };
  });

  const getEndDate = () => {
    return subscriptionEndDate.toLocaleDateString();
  };

  const handleSubscriptionPeriodChange = (event) => {
    const numberOfMonths = parseInt(event.target.value);
    setSubscriptionPeriodSelected(numberOfMonths);

    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + numberOfMonths);
    setSubscriptionEndDate(endDate);
  };

  const { addToast } = useToast();

  // URL search params pour les invitations
  const query = new URLSearchParams(location.search);

  // Gestion des invitations
  const [errorInvitationAccept, setErrorInvitationAccept] = useState(0);

  const acceptInvitation = async () => {
    await mutateSetAffiliateAction({
      id: query.get('invite'),
      action: 'accept',
    });
  };

  const {
    error: errorAction,
    isLoading: isLoadingAction,
    isSuccess: isSuccessAction,
    mutate: mutateSetAffiliateAction,
  } = useMutation(responseInvitationAffiliation, {
    onError: (error) => {
      if (errorInvitationAccept < 5)
        setTimeout(() => {
          acceptInvitation();
          setErrorInvitationAccept(errorInvitationAccept + 1)
        }, 1000);
      else {
        addToast({
          status: 'error',
          autoDelete: false,
          message: (
            <div>
              <div>Nous n'avons pas pu accepter votre invitation automatiquement.</div>
              <div>Veuillez l'accepter depuis votre espace.</div>
            </div>
          )
        });
        push(ROUTES_PATH.SIGN_IN);
      }
    },
  });

  // Mutation pour créer un étudiant
  const {
    error: errorRegistration,
    isLoading: mutateCreateStudentLoading,
    isSuccess: mutateCreateStudentSuccess,
    mutate: mutateCreateStudent,
  } = useMutation(createStudent, {
    onError: async (error) => {
      if (error.request.status === 409) {
        setErrorMessage(
          "L'e-mail ou le n° de téléphone utilisé est déjà associé à un compte."
        );
      }
    },
  });

  // Mutation pour créer une entreprise
  const {
    error: errorRegistrationBusiness,
    isLoading: mutateCreateBusinessLoading,
    isSuccess: mutateCreateBusinessSuccess,
    mutate: mutateCreateBusiness,
  } = useMutation(createBusiness, {
    onError: async (error) => {
      if (error.request.status === 409) {
        setErrorMessage(
          "L'e-mail ou le n° de téléphone utilisé est déjà associé à un compte."
        );
      }
    },
  });

  // Mutation pour créer une école
  const {
    error: errorRegistrationSchool,
    isLoading: mutateCreateSchoolLoading,
    isSuccess: mutateCreateSchoolSuccess,
    mutate: mutateCreateSchool,
  } = useMutation(createSchool, {
    onError: async (error) => {
      if (error.request.status === 409) {
        setErrorMessage(
          "L'e-mail ou le n° de téléphone utilisé est déjà associé à un compte."
        );
      }
    },
  });

  // Add toast when when token is expired
  // Check LS on page load
  let tokenIsExpired = localStorage.getItem('expired');
  useEffect(() => {
    if (tokenIsExpired === "true") {
      addToast({
        status: 'error',
        autoDelete: false,
        message: (
          <div>Votre session a expiré, veuillez vous reconnecter</div>
        ),
      });
      localStorage.removeItem('expired');
    }
  }, []);

  // Gestion des invitations à l'arrivée
  useEffect(() => {
    if (query.get('invite')) {
      addToast({
        title: 'Invitation',
        message: 'Vous devez vous inscrire pour accéder à votre invitation',
        status: 'warning',
      });
    }
  }, []);

  // Gestion du succès d'acceptation d'invitation
  useEffect(() => {
    if (isSuccessAction) {
      addToast({
        status: 'success',
        autoDelete: false,
        message: 'Invitation acceptée'
      });
      push(ROUTES_PATH.SIGN_IN);
    }
  }, [isSuccessAction]);

  // Gestion du succès de création de compte
  useEffect(() => {
    if (mutateCreateStudentSuccess && query.get('invite')) {
      addToast({
        status: 'success',
        autoDelete: false,
        message: (
          <div>
            <div>Création de compte réussie</div>
            <div>Un Mail de confirmation vous a été envoyé.</div>
            <div>Votre invitation va être accepté sous peu.</div>
          </div>
        ),
      });
      acceptInvitation();
    } else if (mutateCreateStudentSuccess || mutateCreateBusinessSuccess || mutateCreateSchoolSuccess) {
      formik.resetForm();
      push(ROUTES_PATH.SIGN_IN + '?accountCreated=true');
    }
  }, [mutateCreateStudentSuccess, mutateCreateBusinessSuccess, mutateCreateSchoolSuccess]);

  const goToSignIn = () => {
    push(ROUTES_PATH.SIGN_IN, { state: { user: accType } });
  };

  const goToHome = () => {
    window.location.href = 'https://linkpick.fr/';
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  // Ajout des états pour l'abonnement et le type de paiement
  const [subscription, setSubscription] = useState('free');
  const [paymentType, setPaymentType] = useState('GoCardLess');

  // Valeurs initiales conditionnelles selon le type d'utilisateur
  const getInitialValues = () => {
    if (accType === 'individual') {
      return {
        email: '',
        password: '',
        repeatPassword: '',
        lastname: '',
        firstname: '',
        phone: '',
        acceptCondition: false,
        knowUs: null,
      };
    } else {
      // Pour school et business
      return {
        email: '',
        password: '',
        repeatPassword: '',
        name: '',
        siret: '',
        phone: '',
        label: '',
        city: '',
        cityCode: '',
        subscription: subscription,
        knowUs: null,
      };
    }
  };

  // Redirect user to GoCardLess subsciption page
  function launchGoCardLess() {

    // Prepare request data to create SEPA mandate
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sepaAmount: paymentAmount,
        userEmail: formik.values.email,
        userName: formik.values.name,
        type: type,
        key: formik.values.password,
        phone: formik.values.phone.slice(0, 3) + formik.values.phone.slice(4),
        siret: formik.values.siret,
        knowUs: formik.values.knowUs,
      }),
      mode: 'cors',
    };

    // Function that open payment link in new navigator tab
    const openInNewTab = (url) => {
      window.open(url, "_blank", "noreferrer");
    };

    // Create the PaymentIntent calling server
    // For local tests
    // fetch('http://127.0.0.1:9090/accounts/subscription/goCardLess', requestOptions)

    // For prod @TODO : Create dedicated service to include token in request
    // Here token is not included in request so it fails
    // So we make request with proxy actually
    // fetch(`${NX_REST_API}/accounts/subscription/goCardLess`, requestOptions)

    // For proxy tests
    fetch(`${NX_PROXY_API}/goCardLess`, requestOptions)

      // Payment intent response
      .then(response => response.json())
      // Retrieve payment link & customer id from Gocardless
      .then(mandateData => {
        const authorisation_url = mandateData.authorisation_url;
        const customer_id = mandateData.customer_id;

        // After SEPA mandate creation
        if (customer_id !== null) {
          // Store customer id and redirect user to payment page 
          openInNewTab(authorisation_url);
          setCustomerId('"' + customer_id + '"');
        }
      }
      );
  }

  // Schéma de validation conditionnel
  const getValidationSchema = () => {
    return accType === 'individual' ? signUpSchema : organismeSchema;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(),
    enableReinitialize: true, // Permet de réinitialiser quand accType change
    onSubmit: async (values) => {
      console.log('Form submitted with values:', values);
      console.log('Form errors:', formik.errors);
      console.log('Form touched:', formik.touched);

      // Vérifier que les mots de passe correspondent
      if (values.password !== values.repeatPassword) {
        setErrorMessage('Les mots de passe ne correspondent pas');
        return;
      }

      if (accType === 'individual') {
        mutateCreateStudent({
          email: values.email,
          phone: values.phone.slice(0, 3) + values.phone.slice(4),
          firstname: values.firstname,
          lastname: values.lastname,
          password: values.password,
          address: '',
          code: '',
          city: '',
          goal: '',
          knowUs: typeof values.knowUs === 'object' && values.knowUs !== null ? values.knowUs.value : values.knowUs || '',
        });
      }

      if (subscription === 'premium') {
        switch (paymentType) {
          case "Stripe":
            setShowStripeContainer(true);
            break;
          case "GoCardLess":
            launchGoCardLess();
            break;
        }
      }

      if (accType === 'business' && subscription === 'free') {
        mutateCreateBusiness({
          email: values.email,
          phone: values.phone.slice(0, 3) + values.phone.slice(4),
          name: values.name,
          siret: values.siret,
          password: values.password,
          address: values.label,
          code: values.cityCode,
          city: values.city,
          subscription: 'free',
          knowUs: typeof values.knowUs === 'object' && values.knowUs !== null ? values.knowUs.value : values.knowUs || '',
        });
      } else if (accType === 'school' && subscription === 'free') {
        mutateCreateSchool({
          email: values.email,
          phone: values.phone.slice(0, 3) + values.phone.slice(4),
          name: values.name,
          siret: values.siret,
          password: values.password,
          address: values.label,
          code: values.cityCode,
          city: values.city,
          subscription: 'free',
          knowUs: typeof values.knowUs === 'object' && values.knowUs !== null ? values.knowUs.value : values.knowUs || '',
        });
      }
    }
  });

  console.log(paymentType)

  const images = [
    // {
    //   src: require('app/libs/styles/src/lib/assets/images/carrousel_sign.png'),
    //   srcSet: require('app/libs/styles/src/lib/assets/images/carrousel_sign.png'),
    //   sizes: "572px"
    // }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change d'image toutes les 5 secondes

    return () => clearInterval(interval);
  }, []);

  // Fonction de validation du téléphone simplifiée
  const handleChangePhone = (e) => {
    const phoneValue = e.target.value;
    formik.setFieldValue('phone', phoneValue);
    setErrorPhone('');
  };

  // Ajout d'un état pour gérer l'étape du formulaire
  const [isLastStep, setIsLastStep] = useState(false);

  // Ajout du state pour les suggestions d'adresse
  const [addresses, setAddresses] = useState([]);
  const getAddressInfo = useMutation(getAddress, {
    onSuccess: (result) => {
      setAddresses(result?.data?.features || []);
    },
  });
  const handleChangeAddress = async (e) => {
    const value = e.target.value;
    formik.setFieldValue('label', value);
    if (!value || value.length < 3) return;
    await getAddressInfo.mutateAsync({ name: value });
  };
  const handleSelectAddress = (option) => {
    formik.setFieldValue('label', option.label);
    formik.setFieldValue('city', option.value.city);
    formik.setFieldValue('cityCode', option.value.postcode);
  };

  const [showStripeContainer, setShowStripeContainer] = useState(false);

  // Callback à appeler après paiement Stripe réussi
  const handleStripeSuccess = () => {
    setShowStripeContainer(false);
    // Soumission du formulaire après paiement
    formik.handleSubmit();
  };

  return (
    <>
      <Head
        subtitle="Créez votre compte Linkpick pour accéder à une plateforme polyvalente dédiée à l'emploi et à la formation."
        title="Inscription"
      />
      <div className={classes["framerDiv"]}>
        <div className={classes["framerDiv2"]}>
          <img
            className={classes["framerImg"]}
            src={CarrouselSign}
            alt="carousel"
            style={{
              transition: 'opacity 0.5s ease-in-out',
              opacity: 1
            }}
          />
        </div>
        <div className={classes["framerDiv3"]}>
          <div className={classes["framerDiv4"]}>
            <img className={classes["framerImg2"]} src={PicsLogo} sizes="274px" alt="linkpick" onClick={goToHome} />
            <div className={classes["framerDiv5"]}>
              <div className={classes["framerP2"]}>
                {accType === 'individual'
                  ? 'Créez votre compte'
                  : accType === 'school'
                    ? 'Inscrivez votre école'
                    : 'Inscrivez votre entreprise'}
              </div>
              <div className={classes["framerP"]}>
                {accType === 'individual'
                  ? 'Rejoignez Linkpick - Votre aventure commence ici !'
                  : 'Rejoignez la communauté Linkpick'}
              </div>
            </div>
            <div className={classes["framerDiv6"]} />
            <div className={classes["framerDiv7"]}>
              {accType === 'individual' ? (
                // Formulaire pour les apprenants
                <>
                  {/* Prénom et Nom côte à côte */}
                  <div className={classes["framerDivRow"]}>
                    <div className={classes["framerDiv8InRow"]}>
                      <div className={classes["framerDivLabel"]}>
                        <div className={classes["framerP3"]}>Prénom</div>
                      </div>
                      <TextField
                        value={formik.values.firstname}
                        onChange={formik.handleChange}
                        name="firstname"
                        error={Boolean(formik.touched.firstname && formik.errors.firstname)}
                      />
                      {formik.touched.firstname && formik.errors.firstname && (
                        <div className={classes["framerError"]}>{formik.errors.firstname}</div>
                      )}
                    </div>
                    <div className={classes["framerDiv8InRow"]}>
                      <div className={classes["framerDivLabel"]}>
                        <div className={classes["framerP3"]}>Nom</div>
                      </div>
                      <TextField
                        value={formik.values.lastname}
                        onChange={formik.handleChange}
                        name="lastname"
                        error={Boolean(formik.touched.lastname && formik.errors.lastname)}
                      />
                      {formik.touched.lastname && formik.errors.lastname && (
                        <div className={classes["framerError"]}>{formik.errors.lastname}</div>
                      )}
                    </div>
                  </div>

                  {/* Email et Téléphone côte à côte */}
                  <div className={classes["framerDivRow"]}>
                    <div className={classes["framerDiv8InRow"]}>
                      <div className={classes["framerDivLabel"]}>
                        <div className={classes["framerP3"]}>Adresse email</div>
                      </div>
                      <TextField
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        name="email"
                        error={Boolean(formik.touched.email && formik.errors.email)}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className={classes["framerError"]}>{formik.errors.email}</div>
                      )}
                    </div>

                    <div className={classes["framerDiv8InRow"]}>
                      <div className={classes["framerDivLabel"]}>
                        <div className={classes["framerP3"]}>Téléphone</div>
                      </div>
                      <PhoneInput
                        value={formik.values.phone}
                        onChange={handleChangePhone}
                        onBlur={formik.handleBlur}
                        name="phone"
                        placeholder=""
                        defaultCountry="FR"
                        error={Boolean((formik.touched.phone && formik.errors.phone) || errorPhone)}
                        className={Boolean((formik.touched.phone && formik.errors.phone) || errorPhone) ? classes["framerErrorInput"] : ""}
                      />
                      {(formik.touched.phone && formik.errors.phone) || errorPhone ? (
                        <div className={classes["framerError"]}>{errorPhone || formik.errors.phone}</div>
                      ) : null}
                    </div>
                  </div>

                  {/* Mot de passe et Confirmation côte à côte */}
                  <div className={classes["framerDivRow"]}>
                    <div className={classes["framerDiv8InRow"]}>
                      <div className={classes["framerDivLabel"]}>
                        <div className={classes["framerP3"]}>Mot de passe</div>
                      </div>
                      <TextField
                        type={showPassword ? 'text' : 'password'}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        name="password"
                        error={Boolean(formik.touched.password && formik.errors.password)}
                        onClick={handleClickShowPassword}
                      />
                      {formik.touched.password && formik.errors.password && (
                        <div className={classes["framerError"]}>{formik.errors.password}</div>
                      )}
                    </div>
                    <div className={classes["framerDiv8InRow"]}>
                      <div className={classes["framerDivLabel"]}>
                        <div className={classes["framerP3"]}>Confirmer le mot de passe</div>
                      </div>
                      <TextField
                        type={showRepeatPassword ? 'text' : 'password'}
                        value={formik.values.repeatPassword}
                        onChange={formik.handleChange}
                        name="repeatPassword"
                        error={Boolean(formik.touched.repeatPassword && formik.errors.repeatPassword)}
                        onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                      />
                      {formik.touched.repeatPassword && formik.errors.repeatPassword && (
                        <div className={classes["framerError"]}>{formik.errors.repeatPassword}</div>
                      )}
                    </div>
                  </div>
                  {/* Bouton Créer mon compte et CGU/RGPD pour individual */}
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <div className={classes["framerDiv10"]}>
                      <Checkbox
                        checked={formik.values.acceptCondition}
                        onChange={(e) => formik.setFieldValue('acceptCondition', e.target.checked)}
                        name="acceptCondition"
                      />
                      <div className={classes["framerP3"]}>
                        J'ai lu et accepte les{' '}
                        <a
                          className={classes["framerText1"]}
                          href="https://linkpick.fr/conditions-generales-utilisation/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          CGU
                        </a>
                        {' '}et la{' '}
                        <a
                          className={classes["framerText1"]}
                          href="https://linkpick.fr/politique-de-confidentialite/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          RGPD
                        </a>
                      </div>
                    </div>
                    {formik.touched.acceptCondition && formik.errors.acceptCondition && (
                      <div className={classes["framerError"]}>
                        {formik.errors.acceptCondition}
                      </div>
                    )}
                  </div>
                  <div
                    className={classes["framerButton2"]}
                    onClick={() => {
                      if (accType === 'business' && subscription === 'premium' && paymentType === 'Stripe') {
                        setShowStripeContainer(true);
                      } else {
                        formik.handleSubmit();
                      }
                    }}
                  >
                    <div className={classes["framerP6"]}>
                      {mutateCreateBusinessLoading || mutateCreateSchoolLoading ? "Chargement..." : "Créer mon compte"}
                    </div>
                  </div>
                </>
              ) : (
                // Formulaire en deux étapes pour business et school
                <>
                  {!isLastStep ? (
                    // Étape 1 : formulaire principal sans les trois sélecteurs
                    <>
                      {/* Nom de l'organisme et SIRET côte à côte */}
                      <div className={classes["framerDivRow"]}>
                        <div className={classes["framerDiv8InRow"]}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>
                              {accType === 'school' ? 'Nom de l\'école' : 'Nom de l\'entreprise'}
                            </div>
                            {formik.touched.name && formik.errors.name && (
                              <div className={classes["framerError"]}>{formik.errors.name}</div>
                            )}
                          </div>
                          <TextField
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            name="name"
                            error={Boolean(formik.touched.name && formik.errors.name)}
                          />
                        </div>
                        <div className={classes["framerDiv8InRow"]}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>SIRET</div>
                            {formik.touched.siret && formik.errors.siret && (
                              <div className={classes["framerError"]}>{formik.errors.siret}</div>
                            )}
                          </div>
                          <TextField
                            value={formik.values.siret}
                            onChange={(e) => {
                              // Permettre seulement les chiffres et limiter à 14 caractères
                              const value = e.target.value.replace(/\D/g, '').slice(0, 14);
                              formik.setFieldValue('siret', value);
                            }}
                            name="siret"
                            placeholder="14 chiffres"
                            error={Boolean(formik.touched.siret && formik.errors.siret)}
                          />
                        </div>
                      </div>
                      {/* Email et Téléphone côte à côte */}
                      <div className={classes["framerDivRow"]}>
                        <div className={classes["framerDiv8InRow"]}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>Adresse email</div>
                            {formik.touched.email && formik.errors.email && (
                              <div className={classes["framerError"]}>{formik.errors.email}</div>
                            )}
                          </div>
                          <TextField
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            name="email"
                            error={Boolean(formik.touched.email && formik.errors.email)}
                          />
                        </div>

                        <div className={classes["framerDiv8InRow"]}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>Téléphone</div>
                            {(formik.touched.phone && formik.errors.phone) || errorPhone ? (
                              <div className={classes["framerError"]}>{errorPhone || formik.errors.phone}</div>
                            ) : null}
                          </div>
                          <PhoneInput
                            value={formik.values.phone}
                            onChange={handleChangePhone}
                            onBlur={formik.handleBlur}
                            name="phone"
                            placeholder=""
                            defaultCountry="FR"
                            error={Boolean((formik.touched.phone && formik.errors.phone) || errorPhone)}
                            className={Boolean((formik.touched.phone && formik.errors.phone) || errorPhone) ? classes["framerErrorInput"] : ""}
                          />
                        </div>
                      </div>
                      {/* Adresse et Ville côte à côte */}
                      <div className={classes["framerDivRow"]}>
                        <div className={classes["framerDiv8"]} style={{ width: '100%' }}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>Adresse</div>
                            {formik.touched.label && formik.errors.label && (
                              <div className={classes["framerError"]}>{formik.errors.label}</div>
                            )}
                          </div>
                          <Autocomplete2
                            options={addresses.map(addr => ({ label: addr.properties.label, value: addr.properties }))}
                            placeholder=""
                            onChange={handleSelectAddress}
                            onInputChange={handleChangeAddress}
                          />
                        </div>
                      </div>
                      {/* Mot de passe et Confirmation côte à côte */}
                      <div className={classes["framerDivRow"]}>
                        <div className={classes["framerDiv8InRow"]}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>Mot de passe</div>
                            {formik.touched.password && formik.errors.password && (
                              <div className={classes["framerError"]}>{formik.errors.password}</div>
                            )}
                          </div>
                          <TextField
                            type={showPassword ? 'text' : 'password'}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            name="password"
                            error={Boolean(formik.touched.password && formik.errors.password)}
                            onClick={handleClickShowPassword}
                          />
                        </div>
                        <div className={classes["framerDiv8InRow"]}>
                          <div className={classes["framerDivLabel"]}>
                            <div className={classes["framerP3"]}>Confirmer le mot de passe</div>
                            {formik.touched.repeatPassword && formik.errors.repeatPassword && (
                              <div className={classes["framerError"]}>{formik.errors.repeatPassword}</div>
                            )}
                          </div>
                          <TextField
                            type={showRepeatPassword ? 'text' : 'password'}
                            value={formik.values.repeatPassword}
                            onChange={formik.handleChange}
                            name="repeatPassword"
                            error={Boolean(formik.touched.repeatPassword && formik.errors.repeatPassword)}
                            onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                          />
                        </div>
                      </div>
                      <div className={classes["framerDiv8"]}>
                        <div className={classes["framerDivLabel"]}>
                          <div className={classes["framerP3"]}>Comment vous nous avez connu ?</div>
                          {formik.touched.knowUs && formik.errors.knowUs && (
                            <div className={classes["framerError"]}>{formik.errors.knowUs}</div>
                          )}
                        </div>
                        <Select
                          value={formik.values.knowUs}
                          onChange={option => formik.setFieldValue('knowUs', option)}
                          name="knowUs"
                          getOptionLabel={option => option.label}
                          getOptionValue={option => option.value}
                          error={Boolean(formik.touched.knowUs && formik.errors.knowUs)}
                          options={KNOW_US}
                          placeholder="Sélectionnez une option"
                          passOptionObject={true}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <div className={classes["framerDiv10"]}>
                          <Checkbox
                            checked={formik.values.acceptCondition}
                            onChange={(e) => formik.setFieldValue('acceptCondition', e.target.checked)}
                            name="acceptCondition"
                          />
                          <div className={classes["framerP3"]}>
                            J'ai lu et accepte les{' '}
                            <a
                              className={classes["framerText1"]}
                              href="https://linkpick.fr/conditions-generales-utilisation/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              CGU
                            </a>
                            {' '}et la{' '}
                            <a
                              className={classes["framerText1"]}
                              href="https://linkpick.fr/politique-de-confidentialite/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              RGPD
                            </a>
                          </div>
                        </div>
                        {formik.touched.acceptCondition && formik.errors.acceptCondition && (
                          <div className={classes["framerError"]}>
                            {formik.errors.acceptCondition}
                          </div>
                        )}
                      </div>
                      {/* Bouton Passer à la dernière étape
                    <div 
                      className={classes["framerButton2"]}
                      onClick={async () => {
                        const errors = await formik.validateForm();
                        formik.setTouched({
                          email: true,
                          password: true,
                          repeatPassword: true,
                          name: true,
                          siret: true,
                          phone: true,
                          label: true,
                          city: true,
                        });
                        // On ne regarde que les erreurs de l'étape 1
                        const step1Fields = ['email', 'password', 'repeatPassword', 'name', 'siret', 'phone', 'label', 'city'];
                        const hasStep1Error = step1Fields.some(field => errors[field]);
                        if (!hasStep1Error) {
                          setIsLastStep(true);
                        }
                      }}
                    >
                      <div className={classes["framerP6"]}>
                        Étape suivante
                      </div>
                    </div>
                    */}
                      <div
                        className={classes["framerButton2"]}
                        onClick={() => {
                          if (accType === 'business' && subscription === 'premium' && paymentType === 'Stripe') {
                            setShowStripeContainer(true);
                          } else {
                            formik.handleSubmit();
                          }
                        }}
                      >
                        <div className={classes["framerP6"]}>
                          {mutateCreateBusinessLoading || mutateCreateSchoolLoading ? "Chargement..." : "Créer mon compte"}
                        </div>
                      </div>
                    </>
                  ) : (
                    // Étape 2 : trois sélecteurs + CGU + bouton Créer mon compte
                    <>
                      {/* Sélecteur Comment vous nous avez connu */}
                      <div className={classes["framerDiv8"]}>
                        <div className={classes["framerDivLabel"]}>
                          <div className={classes["framerP3"]}>Comment vous nous avez connu ?</div>
                          {formik.touched.knowUs && formik.errors.knowUs && (
                            <div className={classes["framerError"]}>{formik.errors.knowUs}</div>
                          )}
                        </div>
                        <Select
                          value={formik.values.knowUs}
                          onChange={option => formik.setFieldValue('knowUs', option)}
                          name="knowUs"
                          getOptionLabel={option => option.label}
                          getOptionValue={option => option.value}
                          error={Boolean(formik.touched.knowUs && formik.errors.knowUs)}
                          options={KNOW_US}
                          placeholder="Sélectionnez une option"
                          passOptionObject={true}
                        />
                      </div>
                      {/* Type d'abonnement */}
                      <div className={classes["framerDiv8"]}>
                        <div className={classes["framerDivLabel"]}>
                          <div className={classes["framerP3"]}>Type d'abonnement</div>
                        </div>
                        <Select
                          value={subscription}
                          onChange={e => setSubscription(e.target.value)}
                          name="subscription"
                          options={[
                            { value: 'premium', label: 'Premium' },
                            { value: 'free', label: 'Sans Abonnement' }
                          ]}
                          placeholder="Choisissez un abonnement"
                        />
                      </div>
                      {/* Type de paiement : affiché seulement si abonnement === 'premium' ET accType === 'business' */}
                      {subscription === 'premium' && accType === 'business' && (
                        <>
                          <div className={classes["framerDiv8"]}>
                            <div className={classes["framerDivLabel"]}>
                              <div className={classes["framerP3"]}>Type de paiement</div>
                            </div>
                            <Select
                              value={paymentType}
                              onChange={e => setPaymentType(e.target.value)}
                              name="paymentType"
                              options={[
                                { value: 'GoCardLess', label: 'Mandat SEPA' },
                                { value: 'Stripe', label: 'Carte bancaire' }
                              ]}
                              placeholder="Choisissez un type de paiement"
                            />
                          </div>
                          {/* Durée d'abonnement : affiché seulement si paiement par carte bancaire */}
                          {paymentType === 'Stripe' && (
                            <div className={classes["framerDiv8"]}>
                              <div className={classes["framerDivLabel"]}>
                                <div className={classes["framerP3"]}>Durée d'abonnement</div>
                              </div>
                              <Select
                                value={subscriptionPeriodSelected}
                                onChange={e => handleSubscriptionPeriodChange(e)}
                                name="subscriptionPeriod"
                                options={subscriptionPeriodOptions}
                                placeholder="Choisissez la durée"
                              />
                            </div>
                          )}
                        </>
                      )}
                      {/* CGU/RGPD + bouton Créer mon compte */}
                      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <div className={classes["framerDiv10"]}>
                          <Checkbox
                            checked={formik.values.acceptCondition}
                            onChange={(e) => formik.setFieldValue('acceptCondition', e.target.checked)}
                            name="acceptCondition"
                          />
                          <div className={classes["framerP3"]}>
                            J'ai lu et accepte les{' '}
                            <a
                              className={classes["framerText1"]}
                              href="https://linkpick.fr/conditions-generales-utilisation/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              CGU
                            </a>
                            {' '}et la{' '}
                            <a
                              className={classes["framerText1"]}
                              href="https://linkpick.fr/politique-de-confidentialite/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              RGPD
                            </a>
                          </div>
                        </div>
                        {formik.touched.acceptCondition && formik.errors.acceptCondition && (
                          <div className={classes["framerError"]}>
                            {formik.errors.acceptCondition}
                          </div>
                        )}
                      </div>
                      <div
                        className={classes["framerButton2"]}
                        onClick={() => {
                          if (accType === 'business' && subscription === 'premium' && paymentType === 'Stripe') {
                            setShowStripeContainer(true);
                          } else {
                            formik.handleSubmit();
                          }
                        }}
                      >
                        <div className={classes["framerP6"]}>
                          {mutateCreateBusinessLoading || mutateCreateSchoolLoading ? "Chargement..." : "Créer mon compte"}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            {/* Message d'erreur global */}
            {(errorRegistration || errorRegistrationBusiness || errorRegistrationSchool || errorMessage || errorPhone) && (
              <div className={classes["framerError"]}>
                {errorPhone || errorMessage || (errorRegistration || errorRegistrationBusiness || errorRegistrationSchool ? 'Une erreur est survenue lors de la création de votre compte' : '\u00A0')}
              </div>
            )}
            {accType === 'individual' && (
              <>
                <div className={classes["framerDiv11"]}>
                  <div className={classes["framerDiv12"]} />
                  <div className={classes["framerP7"]}>Ou s'inscrire avec</div>
                  <div className={classes["framerDiv12"]} />
                </div>
                <div className={classes["framerDiv13"]}>
                  <AuthGoogleLinkedin forceAccountSelection={true} />
                  <AuthGoogleLinkedin variant="linkedin" />
                </div>
              </>
            )}
            <div className={classes["framerP8"]}>
              Vous avez déjà un compte ?
              {' '}
              <strong className={classes["framerText1"]} onClick={goToSignIn}>Se connecter</strong>
            </div>
          </div>
        </div>
      </div>
      {showStripeContainer && (
        <StripeContainer onSuccess={handleStripeSuccess} onClose={() => setShowStripeContainer(false)} />
      )}
    </>
  );
}

const styles = () => {
  const { fontSizes2, gap, padding, iconSizes, colors } = linkpickTheme.params;

  return {
    "framerDiv": {
      alignItems: "center",
      backgroundColor: colors.blanc,
      display: "flex",
      flexDirection: "row",
      flexGrow: "1",
      height: "100vh",
      justifyContent: "flex-start",
      position: "relative",
      overflow: "hidden",
      "@media (max-width: 810px)": {
        height: "auto",
        minHeight: "100vh",
        overflow: "visible",
        alignItems: "flex-start",
      },
    },
    "framerDiv2": {
      backgroundImage: `linear-gradient(${colors.blancGlacial} 0%, ${colors.bleuPale} 100%)`,
      display: "flex",
      flexGrow: "1",
      height: "100%",
      width: "45%",
      placeItems: "center",
      justifyContent: "center",
      "@media (max-width: 810px)": {
        display: "none",
      },
    },
    "framerImg": {
      width: "45%",
    },
    "framerP": {
      ...combine(fontSizes2.bodyLg),
      fontFamily: "\"Roboto\", sans-serif",
      textAlign: "center",
      color: colors.bleuArdoise,
      width: "100%",
    },
    "framerDiv3": {
      ...combine(gap['3xl']),
      alignContent: "center",
      display: "flex",
      flexDirection: "column",
      flexGrow: "1",
      height: "min-content",
      position: "relative",
      placeItems: "center",
      overflow: "visible",
      "@media (max-width: 810px)": {
        placeItems: "flex-start",
        justifyContent: "flex-start",
        alignContent: "flex-start",
        paddingTop: "20px",
      },
    },
    "framerDiv4": {
      ...combine(gap.xl),
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "550px",
      overflow: "visible",
      "@media (max-width: 1439px)": {
        width: "317px",
      },
      "@media (min-width: 1440px) and (max-width: 1542px)": {
        width: "430px",
      },
      "@media (min-width: 1543px) and (max-width: 1919px)": {
        width: "500px",
      },
      "@media (max-width: 810px)": {
        width: "100%",
        padding: "42px",
      },
    },
    "framerImg2": {
      aspectRatio: "4.38134 / 1",
      position: "relative",
      width: "274px",
      overflow: "visible",
      cursor: "pointer",
      "@media (max-width: 1439px)": {
        width: "198px",
      },
      "@media (min-width: 1440px) and (max-width: 1542px)": {
        width: "198px",
      },
      "@media (min-width: 1543px) and (max-width: 1919px)": {
        width: "230px",
      },
    },
    "framerDiv5": {
      ...combine(gap.s),
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "100%",
    },
    "framerP2": {
      ...combine(fontSizes2.h1),
      fontFamily: "\"Montserrat\", sans-serif",
      fontWeight: "600",
      textAlign: "center",
      color: colors.bleuMarine,
    },
    "framerDiv6": {
      backgroundColor: colors.bleuPale,
      height: "1px",
      position: "relative",
      width: "100%",
    },
    "framerDiv7": {
      ...combine(gap.m),
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "100%",
    },
    "framerDiv8": {
      ...combine(gap.xs),
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "100%",
    },
    "framerDivRow": {
      ...combine(gap.m),
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "row",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "100%",
      "@media (max-width: 768px)": {
        flexDirection: "column",
        gap: gap.s,
      },
    },
    "framerDiv8InRow": {
      ...combine(gap.xs),
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      flex: "1 1 calc(50% - 12px)",
      maxWidth: "calc(50% - 12px)",
      minWidth: "0",
      "@media (max-width: 768px)": {
        flex: "1 1 100%",
        maxWidth: "100%",
      },
    },
    "framerP3": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Roboto\", sans-serif",
      color: colors.blancGlacier,
    },
    "framerDiv9": {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      height: "min-content",
      justifyContent: "space-between",
      position: "relative",
      width: "100%",
    },
    "framerDiv10": {
      ...combine(gap.xs),
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
    },
    "framerIcon7": {
      ...combine(iconSizes.iconSm),
      position: "relative",
      fill: colors.bleuElectrique,
    },
    "framerP5": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Montserrat\", sans-serif",
      fontWeight: "500",
      color: colors.bleuMarine,
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "framerButton2": {
      ...combine(padding.s, padding.m),
      alignContent: "center",
      backgroundColor: colors.bleuElectrique,
      borderRadius: "175px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      height: "40px",
      position: "relative",
      width: "100%",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: colors.bleuSaphir,
      },
    },
    "framerP6": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Montserrat\", sans-serif",
      fontWeight: "500",
      lineHeight: "23.625px",
      textAlign: "center",
      color: colors.blanc,
    },
    "framerDiv11": {
      ...combine(gap.s),
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "100%",
    },
    "framerDiv12": {
      backgroundColor: colors.bleuPale,
      flexGrow: "1",
      height: "1px",
      position: "relative",
    },
    "framerP7": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Montserrat\", sans-serif",
      fontWeight: "500",
      color: colors.bleuPale,
    },
    "framerDiv13": {
      ...combine(gap.s),
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "row",
      height: "min-content",
      justifyContent: "flex-start",
      position: "relative",
      width: "100%",
      overflow: "visible",
    },
    "framerP8": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Montserrat\", sans-serif",
      fontWeight: "500",
      textAlign: "center",
      color: colors.bleuMarine,
      width: "100%",
    },
    "framerText1": {
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "framerDivLabel": {
      ...combine(gap.xs),
      display: "flex",
      flexDirection: "column",
      width: "100%",
      marginBottom: "4px",
    },
    "framerError": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Roboto\", sans-serif",
      color: colors.roseElectrique,
      wordWrap: 'break-word',
      whiteSpace: 'normal',
      maxWidth: '100%',
      overflow: 'hidden',
      lineHeight: '1.2',
      fontSize: '12px',
    },
    subscriptionInfo: {
      ...combine(fontSizes2.body),
      marginTop: '16px',
      color: colors.bleuArdoise,
      lineHeight: '1.5',
      '& p': {
        marginBottom: '8px',
        '&:last-child': {
          marginBottom: 0
        }
      }
    },
    "framerErrorInput": {
      border: "1px solid #FF4D6D",
      backgroundColor: "#FFE6EA",
      color: "#FF4D6D",
    },
  };
};